import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import "@progressive-development/pd-icon/pd-icon";
import "@progressive-development/pd-forms/pd-button";
import { pdIcons } from "@progressive-development/pd-icon";
import { formatShortDate } from "@progressive-development/pd-utils";
//#region src/pd-mail/PdMailItem.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/** Icon per delivery state (marker on the left of each row). */
var stateIcons = {
	SUCCESS: pdIcons.ICON_CHECKBOX_ONLY_CHECK,
	ERROR: pdIcons.ICON_XCLOSE,
	PENDING: pdIcons.ICON_CLOCK,
	PROCESSING: pdIcons.ICON_CLOCK,
	RETRY: pdIcons.ICON_CLOCK
};
var PdMailItem = class PdMailItem extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.withResend = false;
		this._expanded = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      container-type: inline-size;
    }

    .row {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.6rem 0;
    }

    .state-marker {
      flex: none;
      width: var(--pd-mail-marker-size, 32px);
      height: var(--pd-mail-marker-size, 32px);
      border-radius: 50%;
      display: grid;
      place-items: center;
      background: color-mix(in srgb, var(--_state-col) 15%, transparent);
      --pd-icon-col: var(--_state-col);
      --pd-icon-size: calc(var(--pd-mail-marker-size, 32px) * 0.55);
    }

    .state--SUCCESS {
      --_state-col: var(
        --pd-mail-state-success-col,
        var(--pd-default-success-col, #2e7d32)
      );
    }
    .state--ERROR {
      --_state-col: var(
        --pd-mail-state-error-col,
        var(--pd-default-error-col, #c62828)
      );
    }
    .state--PENDING,
    .state--PROCESSING,
    .state--RETRY {
      --_state-col: var(
        --pd-mail-state-pending-col,
        var(--pd-default-font-muted-col, #757575)
      );
    }

    .body {
      flex: 1;
      min-width: 0;
    }

    .title-line {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .title {
      font-weight: 600;
      color: var(--pd-default-font-title-col, inherit);
    }

    /* Technical template name (no app mapping) — visibly technical. */
    .title--technical {
      font-family: monospace;
      font-weight: 500;
    }

    time,
    .recipient-line,
    .detail {
      font-size: var(--pd-mail-font-size-sub, 0.85em);
      color: var(--pd-default-font-muted-col, #757575);
    }

    time {
      white-space: nowrap;
    }

    .error-line {
      font-size: var(--pd-mail-font-size-sub, 0.85em);
      color: var(
        --pd-mail-state-error-col,
        var(--pd-default-error-col, #c62828)
      );
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    button.toggle {
      font: inherit;
      border: none;
      background: none;
      padding: 0.25rem 0;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      cursor: pointer;
      color: var(--pd-default-font-link-col, inherit);
      font-size: var(--pd-mail-font-size-sub, 0.85em);
      min-height: 2rem;
    }

    button.toggle:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col, currentColor);
      outline-offset: 2px;
    }

    .chevron {
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(90deg);
    }

    .detail {
      margin: 0.25rem 0 0.5rem;
    }

    .message-text {
      margin: 0 0 0.5rem;
      line-height: 1.5;
      white-space: pre-line;
      color: var(--pd-default-font-content-col, #333);
    }

    /* Escaped HTML source — never rendered as markup. */
    .html-source {
      margin: 0 0 0.5rem;
      padding: 0.5rem;
      font-family: monospace;
      font-size: 0.9em;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      max-height: 16rem;
      overflow-y: auto;
      background: var(--pd-default-bg-light-col, #f8f9fc);
      border-radius: var(--pd-radius-md, 0.5rem);
    }

    .meta {
      display: grid;
      grid-template-columns: max-content 1fr;
      gap: 0.15rem 1rem;
    }

    .meta-label {
      color: var(--pd-default-font-muted-col, #757575);
    }

    .meta-value {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .meta-value--error {
      color: var(
        --pd-mail-state-error-col,
        var(--pd-default-error-col, #c62828)
      );
    }

    .resend {
      margin-top: 0.5rem;
    }

    @container (max-width: 480px) {
      .title-line time {
        flex-basis: 100%;
      }

      .meta {
        grid-template-columns: 1fr;
        gap: 0.1rem;
      }

      .meta-label {
        margin-top: 0.35rem;
      }
    }
  `;
	}
	render() {
		const entry = this.entry;
		if (!entry) return nothing;
		const deliveryState = this._deliveryState(entry);
		return html`
      <div class="row">
        <span class="state-marker state--${deliveryState}" aria-hidden="true">
          <pd-icon icon="${stateIcons[deliveryState]}"></pd-icon>
        </span>
        <div class="body">
          <div class="title-line">
            ${this._renderTitle(entry)}
            <time datetime="${entry.creation}" title="${entry.creation}">
              ${formatShortDate(entry.creation, true)}
            </time>
          </div>
          <div class="recipient-line">
            ${msg("an", { id: "pd.mail.to" })} ${entry.to}
            ${entry.cc || entry.bcc ? html`<span>
                    · ${msg("CC/BCC", { id: "pd.mail.ccHint" })}</span
                  >` : nothing}
            · ${this._stateLabel(deliveryState)}
          </div>
          ${deliveryState === "ERROR" && entry.delivery?.error ? html`<div class="error-line" title="${entry.delivery.error}">
                  ${entry.delivery.error.split("\n")[0]}
                </div>` : nothing}
          <button
            class="toggle"
            aria-expanded="${this._expanded}"
            aria-controls="detailId"
            @click="${() => this._expanded = !this._expanded}"
          >
            <span
              class="chevron ${this._expanded ? "open" : ""}"
              aria-hidden="true"
              >▸</span
            >
            ${msg("Details", { id: "pd.mail.details" })}
          </button>
          ${this._expanded ? this._renderDetails(entry, deliveryState) : nothing}
        </div>
      </div>
    `;
	}
	/** Missing delivery = the extension has not picked the document up yet. */
	_deliveryState(entry) {
		return entry.delivery?.state ?? "PENDING";
	}
	_stateLabel(deliveryState) {
		switch (deliveryState) {
			case "SUCCESS": return msg("Gesendet", { id: "pd.mail.state.success" });
			case "ERROR": return msg("Fehlgeschlagen", { id: "pd.mail.state.error" });
			case "PROCESSING": return msg("Wird gesendet", { id: "pd.mail.state.processing" });
			case "RETRY": return msg("Neuer Versuch", { id: "pd.mail.state.retry" });
			case "PENDING": return msg("Wartet", { id: "pd.mail.state.pending" });
		}
	}
	/** Subject; template mails carry no subject client-side — the mapped
	* template label steps in (unmapped: technical name, visibly mono). */
	_renderTitle(entry) {
		if (entry.message?.subject) return html`<span class="title">${entry.message.subject}</span>`;
		const name = entry.template?.name ?? "";
		const label = this.templateLabels?.[name];
		return label ? html`<span class="title">${label}</span>` : html`<span class="title title--technical">${name}</span>`;
	}
	_renderDetails(entry, deliveryState) {
		return html`
      <div class="detail" id="detailId">
        ${this._renderContent(entry)}
        <div class="meta">
          ${this._metaRow(msg("Empfänger", { id: "pd.mail.meta.to" }), entry.to)}
          ${this._metaRow("CC", entry.cc)} ${this._metaRow("BCC", entry.bcc)}
          ${this._metaRow(msg("Antwort an", { id: "pd.mail.meta.replyTo" }), entry.replyTo)}
          ${this._metaRow(msg("Versuche", { id: "pd.mail.meta.attempts" }), entry.delivery?.attempts?.toString())}
          ${this._metaRow(msg("Abgeschlossen", { id: "pd.mail.meta.endTime" }), entry.delivery?.endTime ? formatShortDate(entry.delivery.endTime, true) : void 0)}
          ${this._metaRow("Message-ID", entry.delivery?.messageId)}
          ${this._extraRows(entry)}
          ${entry.delivery?.error ? html`
                  <span class="meta-label">
                    ${msg("Fehler", { id: "pd.mail.meta.error" })}
                  </span>
                  <span class="meta-value meta-value--error">
                    ${entry.delivery.error}
                  </span>
                ` : nothing}
        </div>
        ${this.withResend && deliveryState === "ERROR" ? html`
                <pd-button
                  class="resend"
                  .text="${msg("Erneut senden", { id: "pd.mail.resend" })}"
                  @click="${this._handleResend}"
                ></pd-button>
              ` : nothing}
      </div>
    `;
	}
	/** Message text preferred; HTML only as escaped source, never rendered.
	*  Template mails show their data as key-value rows (the payload IS
	*  the message there). */
	_renderContent(entry) {
		if (entry.message?.text) return html`<p class="message-text">${entry.message.text}</p>`;
		if (entry.message?.html) return html`<pre class="html-source">${entry.message.html}</pre>`;
		if (entry.template) return html`
        <div class="meta">
          ${Object.entries(entry.template.data).map(([key, value]) => html`
              <span class="meta-label">
                ${this.fieldLabels?.[key] ?? key}
              </span>
              <span class="meta-value">${this._formatValue(value)}</span>
            `)}
        </div>
      `;
		return nothing;
	}
	_formatValue(value) {
		if (value === null || value === void 0 || value === "") return "—";
		if (typeof value === "object") return JSON.stringify(value);
		return String(value);
	}
	_metaRow(label, value) {
		if (!value) return nothing;
		return html`
      <span class="meta-label">${label}</span>
      <span class="meta-value">${value}</span>
    `;
	}
	_extraRows(entry) {
		if (!this.extraLabels) return nothing;
		return html`${Object.entries(this.extraLabels).map(([key, label]) => {
			const value = entry[key];
			if (value === void 0 || value === null || value === "") return nothing;
			return html`
        <span class="meta-label">${label}</span>
        <span class="meta-value">${this._formatValue(value)}</span>
      `;
		})}`;
	}
	_handleResend() {
		this.dispatchEvent(new CustomEvent("pd-mail-resend", {
			detail: { mail: this.entry },
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ attribute: false })], PdMailItem.prototype, "entry", void 0);
__decorate([property({ attribute: false })], PdMailItem.prototype, "templateLabels", void 0);
__decorate([property({ attribute: false })], PdMailItem.prototype, "fieldLabels", void 0);
__decorate([property({ attribute: false })], PdMailItem.prototype, "extraLabels", void 0);
__decorate([property({ type: Boolean })], PdMailItem.prototype, "withResend", void 0);
__decorate([state()], PdMailItem.prototype, "_expanded", void 0);
PdMailItem = __decorate([localized()], PdMailItem);
//#endregion
export { PdMailItem };
